/**
 * Helpers for detecting the "trial usage limit reached" 403 response that
 * Galactus' `TrialUsageLimitMiddleware` returns from endpoints marked with
 * `[EnforceTrialUsageLimits]` (currently `/v1/calls`,
 * `/outbound/campaigns` create, and `/outbound/campaigns/{id}/start`).
 *
 * Galactus returns a `ProblemDetails` payload. Once the machine-readable
 * `errorCode` / `code` / `type` / `extensions.code` field is rolled out we
 * match on it; during rollout we also tolerate the legacy English substring
 * so in-flight deploys still trigger the correct UX.
 */

export const TRIAL_LIMIT_ERROR_CODE = "TRIAL_USAGE_LIMIT_REACHED";

type ProblemDetailsLike = {
  detail?: unknown;
  title?: unknown;
  errorCode?: unknown;
  code?: unknown;
  type?: unknown;
  extensions?: { code?: unknown } | null;
};

function stringOrEmpty(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function hasTrialLimitCode(body: ProblemDetailsLike): boolean {
  const candidates = [
    stringOrEmpty(body.errorCode),
    stringOrEmpty(body.code),
    stringOrEmpty(body.type),
    stringOrEmpty(body.extensions?.code),
  ];
  return candidates.some((c) => c === TRIAL_LIMIT_ERROR_CODE);
}

function hasTrialLimitDetail(body: ProblemDetailsLike): boolean {
  const detail = stringOrEmpty(body.detail).toLowerCase();
  return detail.includes("trial usage limit");
}

/**
 * Narrow test that matches Galactus' trial-limit 403 response. Prefers the
 * machine-readable `errorCode` when present, falls back to a loose substring
 * match on `detail` for backward compat during the backend rollout window.
 */
export function isTrialLimitResponse(status: number, data: unknown): boolean {
  if (status !== 403) return false;
  if (!data || typeof data !== "object") return false;
  const body = data as ProblemDetailsLike;
  return hasTrialLimitCode(body) || hasTrialLimitDetail(body);
}

/** Thrown when a write failed because the org hit its trial usage limit. */
export class TrialLimitError extends Error {
  readonly code = TRIAL_LIMIT_ERROR_CODE;
  readonly responseBody: unknown;
  constructor(responseBody: unknown) {
    super("Trial usage limit reached");
    this.name = "TrialLimitError";
    this.responseBody = responseBody;
  }
}
